import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import AuthenticatedHeader from '../../components/ui/AuthenticatedHeader';
import TabNavigation from '../../components/ui/TabNavigation';
import HistoryCard from './components/HistoryCard';
import HistoryFilters from './components/HistoryFilters';
import EditingIndicator from './components/EditingIndicator';
import EmptyHistoryState from './components/EmptyHistoryState';

const History = () => {
  const [historyData, setHistoryData] = useState([]);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [completionFilter, setCompletionFilter] = useState('all');

  // Mock history data
  useEffect(() => {
    const mockHistoryData = [
      {
        date: '2024-09-24',
        goals: {
          '5Year': [
            { id: 1, text: 'Build a sustainable business that impacts 10,000+ lives', completed: false, createdAt: '2024-09-24T06:00:00Z' },
            { id: 2, text: 'Achieve financial independence through multiple income streams', completed: true, createdAt: '2024-09-24T06:00:00Z' }
          ],
          '90Day': [
            { id: 3, text: 'Launch MVP of personal development platform', completed: true, createdAt: '2024-09-24T06:00:00Z' },
            { id: 4, text: 'Complete advanced React certification course', completed: false, createdAt: '2024-09-24T06:00:00Z' }
          ],
          'Daily': [
            { id: 5, text: 'Review and update project roadmap', completed: true, createdAt: '2024-09-24T06:00:00Z' },
            { id: 6, text: 'Complete 2 hours of focused coding', completed: true, createdAt: '2024-09-24T06:00:00Z' },
            { id: 7, text: 'Send follow-up emails to potential clients', completed: false, createdAt: '2024-09-24T06:00:00Z' }
          ]
        },
        core4: {
          faith: { goal: 'Read 30 minutes of spiritual text and meditate', completed: true },
          family: { goal: 'Have meaningful conversation with spouse about future plans', completed: true },
          finances: { goal: 'Review monthly budget and track all expenses', completed: false },
          fitness: { goal: 'Complete 45-minute strength training workout', completed: true }
        }
      },
      {
        date: '2024-09-23',
        goals: {
          '5Year': [
            { id: 8, text: 'Build a sustainable business that impacts 10,000+ lives', completed: false, createdAt: '2024-09-23T06:00:00Z' },
            { id: 9, text: 'Achieve financial independence through multiple income streams', completed: false, createdAt: '2024-09-23T06:00:00Z' }
          ],
          '90Day': [
            { id: 10, text: 'Launch MVP of personal development platform', completed: false, createdAt: '2024-09-23T06:00:00Z' },
            { id: 11, text: 'Complete advanced React certification course', completed: true, createdAt: '2024-09-23T06:00:00Z' }
          ],
          'Daily': [
            { id: 12, text: 'Research competitor analysis for platform features', completed: true, createdAt: '2024-09-23T06:00:00Z' },
            { id: 13, text: 'Write blog post about personal development journey', completed: true, createdAt: '2024-09-23T06:00:00Z' }
          ]
        },
        core4: {
          faith: { goal: 'Morning prayer and gratitude journaling', completed: true },
          family: { goal: 'Plan weekend family activity', completed: true },
          finances: { goal: 'Research investment opportunities for Q4', completed: true },
          fitness: { goal: 'Go for 5-mile run in the morning', completed: false }
        }
      },
      {
        date: '2024-09-22',
        goals: {
          '5Year': [
            { id: 14, text: 'Build a sustainable business that impacts 10,000+ lives', completed: false, createdAt: '2024-09-22T06:00:00Z' }
          ],
          '90Day': [
            { id: 15, text: 'Launch MVP of personal development platform', completed: false, createdAt: '2024-09-22T06:00:00Z' },
            { id: 16, text: 'Complete advanced React certification course', completed: false, createdAt: '2024-09-22T06:00:00Z' }
          ],
          'Daily': [
            { id: 17, text: 'Set up development environment for new project', completed: true, createdAt: '2024-09-22T06:00:00Z' },
            { id: 18, text: 'Create wireframes for user dashboard', completed: true, createdAt: '2024-09-22T06:00:00Z' },
            { id: 19, text: 'Schedule meetings with potential mentors', completed: false, createdAt: '2024-09-22T06:00:00Z' }
          ]
        },
        core4: {
          faith: { goal: 'Attend evening service and connect with community', completed: true },
          family: { goal: 'Cook dinner together and share daily highlights', completed: true },
          finances: { goal: 'Update financial tracking spreadsheet', completed: true },
          fitness: { goal: 'Complete yoga session and stretching routine', completed: true }
        }
      },
      {
        date: '2024-09-20',
        goals: {
          '5Year': [
            { id: 20, text: 'Build a sustainable business that impacts 10,000+ lives', completed: false, createdAt: '2024-09-20T06:00:00Z' }
          ],
          '90Day': [
            { id: 21, text: 'Launch MVP of personal development platform', completed: false, createdAt: '2024-09-20T06:00:00Z' }
          ],
          'Daily': [
            { id: 22, text: 'Research market trends and user needs', completed: true, createdAt: '2024-09-20T06:00:00Z' },
            { id: 23, text: 'Draft business plan outline', completed: false, createdAt: '2024-09-20T06:00:00Z' }
          ]
        },
        core4: {
          faith: { goal: 'Morning meditation and reflection', completed: false },
          family: { goal: 'Call parents and check in on their week', completed: true },
          finances: { goal: 'Review and categorize recent expenses', completed: false },
          fitness: { goal: 'Take long walk in nature', completed: true }
        }
      }
    ];

    setHistoryData(mockHistoryData);
  }, []);

  // Calculate editable entries (within 72 hours)
  const editableEntries = useMemo(() => {
    const now = new Date();
    const editingWindow = 72 * 60 * 60 * 1000; // 72 hours in milliseconds
    
    return historyData?.filter(entry => {
      const entryDate = new Date(entry.date);
      return (now - entryDate) <= editingWindow;
    });
  }, [historyData]);

  // Filter and search functionality
  const filteredData = useMemo(() => {
    let filtered = [...historyData];

    // Date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      let cutoffDate;
      
      switch (dateRange) {
        case 'week':
          cutoffDate = new Date(now - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          cutoffDate = new Date(now - 30 * 24 * 60 * 60 * 1000);
          break;
        case '3months':
          cutoffDate = new Date(now - 90 * 24 * 60 * 60 * 1000);
          break;
        default:
          cutoffDate = new Date(0);
      }
      
      filtered = filtered?.filter(entry => new Date(entry.date) >= cutoffDate);
    }

    // Completion filter
    if (completionFilter !== 'all') {
      filtered = filtered?.filter(entry => {
        const totalGoals = Object.values(entry?.goals)?.reduce((sum, goals) => sum + goals?.length, 0);
        const completedGoals = Object.values(entry?.goals)?.reduce(
          (sum, goals) => sum + goals?.filter(g => g?.completed)?.length, 
          0
        );
        const core4Completed = Object.values(entry?.core4)?.filter(area => area?.completed)?.length;
        
        const goalCompletionRate = totalGoals > 0 ? completedGoals / totalGoals : 0;
        const core4CompletionRate = core4Completed / 4;
        const overallRate = (goalCompletionRate + core4CompletionRate) / 2;
        
        switch (completionFilter) {
          case 'completed':
            return overallRate >= 0.8;
          case 'partial':
            return overallRate >= 0.3 && overallRate < 0.8;
          case 'incomplete':
            return overallRate < 0.3;
          default:
            return true;
        }
      });
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm?.toLowerCase();
      filtered = filtered?.filter(entry => {
        // Search in goals
        const goalMatches = Object.values(entry?.goals)?.some(goals =>
          goals?.some(goal => goal?.text?.toLowerCase()?.includes(searchLower))
        );
        
        // Search in CORE4
        const core4Matches = Object.values(entry?.core4)?.some(area =>
          area?.goal?.toLowerCase()?.includes(searchLower)
        );
        
        // Search in date
        const dateMatches = entry?.date?.includes(searchTerm);
        
        return goalMatches || core4Matches || dateMatches;
      });
    }

    return filtered;
  }, [historyData, searchTerm, dateRange, completionFilter]);

  const handleToggleExpanded = (date) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded?.has(date)) {
      newExpanded?.delete(date);
    } else {
      newExpanded?.add(date);
    }
    setExpandedCards(newExpanded);
  };

  const handleUpdateSnapshot = (updatedSnapshot) => {
    setHistoryData(prev => 
      prev?.map(entry => 
        entry?.date === updatedSnapshot?.date ? updatedSnapshot : entry
      )
    );
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDateRange('all');
    setCompletionFilter('all');
  };

  const isEditable = (date) => {
    const now = new Date();
    const entryDate = new Date(date);
    const editingWindow = 72 * 60 * 60 * 1000; // 72 hours
    return (now - entryDate) <= editingWindow;
  };

  const hasActiveFilters = searchTerm || dateRange !== 'all' || completionFilter !== 'all';

  return (
    <>
      <Helmet>
        <title>History - Aletheos Daily</title>
        <meta name="description" content="Review your goal-setting history and track your progress over time with detailed daily snapshots." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <AuthenticatedHeader userName="Sarah Johnson" userRole="client" />
        
        <main className="pb-20 lg:pb-8">
          <div className="max-w-4xl mx-auto px-4 lg:px-6 py-6">
            {/* Page Header */}
            <div className="mb-6">
              <h1 className="text-2xl lg:text-3xl font-heading font-bold text-foreground mb-2">
                Goal History
              </h1>
              <p className="text-muted-foreground">
                Review your progress and make corrections to recent entries
              </p>
            </div>

            {/* Editing Window Indicator */}
            <EditingIndicator 
              editableCount={editableEntries?.length}
              totalCount={historyData?.length}
            />

            {/* Filters */}
            <div className="mb-6">
              <HistoryFilters
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                completionFilter={completionFilter}
                onCompletionFilterChange={setCompletionFilter}
                onClearFilters={handleClearFilters}
                resultCount={filteredData?.length}
              />
            </div>

            {/* History Content */}
            {filteredData?.length === 0 ? (
              <EmptyHistoryState 
                hasFilters={hasActiveFilters}
                onClearFilters={handleClearFilters}
              />
            ) : (
              <div className="space-y-4">
                {filteredData?.map((snapshot) => (
                  <HistoryCard
                    key={snapshot?.date}
                    snapshot={snapshot}
                    isEditable={isEditable(snapshot?.date)}
                    onUpdateSnapshot={handleUpdateSnapshot}
                    onToggleExpanded={handleToggleExpanded}
                    isExpanded={expandedCards?.has(snapshot?.date)}
                  />
                ))}
              </div>
            )}
          </div>
        </main>

        {/* Mobile Navigation */}
        <TabNavigation />
      </div>
    </>
  );
};

export default History;